import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { salesAPI } from '../services/api';
import { DollarSign, TrendingUp, Receipt, Plus, FileText, ArrowUpDown, AlertCircle } from 'lucide-react';

const SalesPage = () => {
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('date-desc');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setError(null);
      const response = await salesAPI.getAll();
      setSales(response.data.sales || []);
      setAnalytics({
        totalRevenue: response.data.total_revenue || 0,
        totalProfit: response.data.total_profit || 0,
        avgProfitMargin: response.data.avg_profit_margin || 0,
        totalSales: response.data.total || 0,
      });
    } catch (error) {
      console.error('Error fetching sales:', error);
      setError('Failed to load sales data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (value) => {
    return parseFloat(value || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Memoized sorted sales
  const sortedSales = useMemo(() => {
    const salesCopy = [...sales];
    switch (sortBy) {
      case 'date-desc':
        return salesCopy.sort((a, b) => new Date(b.sale_date || b.created_at) - new Date(a.sale_date || a.created_at));
      case 'date-asc':
        return salesCopy.sort((a, b) => new Date(a.sale_date || a.created_at) - new Date(b.sale_date || b.created_at));
      case 'price-high':
        return salesCopy.sort((a, b) => parseFloat(b.sale_price) - parseFloat(a.sale_price));
      case 'price-low':
        return salesCopy.sort((a, b) => parseFloat(a.sale_price) - parseFloat(b.sale_price));
      case 'profit-high':
        return salesCopy.sort((a, b) => parseFloat(b.profit) - parseFloat(a.profit));
      case 'profit-low':
        return salesCopy.sort((a, b) => parseFloat(a.profit) - parseFloat(b.profit));
      case 'margin-high':
        return salesCopy.sort((a, b) => parseFloat(b.profit_margin) - parseFloat(a.profit_margin));
      default:
        return salesCopy;
    }
  }, [sales, sortBy]);

  // Memoized stat cards
  const statCards = useMemo(() => [
    {
      title: 'Total Revenue',
      value: `$${formatCurrency(analytics?.totalRevenue || 0)}`,
      icon: DollarSign,
      gradient: 'from-emerald-500 to-emerald-600',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
    },
    {
      title: 'Total Profit',
      value: `$${formatCurrency(analytics?.totalProfit || 0)}`,
      subtitle: `${(analytics?.avgProfitMargin || 0).toFixed(1)}% margin`,
      icon: TrendingUp,
      gradient: 'from-green-500 to-green-600',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
    },
    {
      title: 'Total Sales',
      value: analytics?.totalSales || 0,
      subtitle: 'Completed transactions',
      icon: Receipt,
      gradient: 'from-slate-800 to-slate-900',
      iconBg: 'bg-slate-100',
      iconColor: 'text-slate-900',
    },
  ], [analytics]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container flex flex-col items-center justify-center min-h-[50vh] text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Error loading sales</h3>
        <p className="text-slate-600 mb-6">{error}</p>
        <button onClick={fetchData} className="btn-primary">Try again</button>
      </div>
    );
  }

  return (
    <div className="page-container space-y-6">
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Sales</h1>
          <p className="page-subtitle">Track your sales and revenue</p>
        </div>
        <button onClick={() => navigate('/sales/record')} className="btn-success">
          <Plus className="w-5 h-5" />
          Record Sale
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {statCards.map((stat, index) => (
          <div key={index} className="card card-body">
            <div className="flex items-center justify-between">
              <div className={`p-3 rounded-xl ${stat.iconBg}`}>
                <stat.icon className={`w-6 h-6 ${stat.iconColor}`} />
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-sm font-medium text-slate-600">{stat.title}</p>
                {stat.subtitle && <p className="text-xs text-slate-500">{stat.subtitle}</p>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {sales.length > 0 && (
        <div className="card card-body flex flex-row flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-slate-600">
            Showing <span className="font-semibold text-slate-900">{sales.length}</span> sales
          </p>
          <div className="flex items-center gap-2">
            <ArrowUpDown className="w-4 h-4 text-slate-400" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="input-field py-2 max-w-[180px]"
            >
              <option value="date-desc">Newest First</option>
              <option value="date-asc">Oldest First</option>
              <option value="price-high">Price: High to Low</option>
              <option value="price-low">Price: Low to High</option>
              <option value="profit-high">Profit: High to Low</option>
              <option value="profit-low">Profit: Low to High</option>
              <option value="margin-high">Margin: High to Low</option>
            </select>
          </div>
        </div>
      )}

      {sales.length === 0 ? (
        <div className="card card-body text-center py-16">
          <DollarSign className="w-14 h-14 text-slate-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-slate-900 mb-1">No sales yet</h3>
          <p className="text-slate-500 mb-6">Record your first sale to start tracking revenue</p>
          <button onClick={() => navigate('/sales/record')} className="btn-success">Record first sale</button>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="table-container">
            <table className="table-modern">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Watch</th>
                  <th>Client</th>
                  <th>Sale Price</th>
                  <th>Profit</th>
                  <th>Margin</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedSales.map((sale) => (
                  <tr
                    key={sale.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/sales/${sale.id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Receipt className="w-4 h-4 text-gray-400" />
                        <span className="font-mono text-sm font-medium text-gray-900">
                          {sale.invoice_number}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{sale.watch_brand}</div>
                      <div className="text-sm text-gray-500">{sale.watch_model}</div>
                      {sale.watch_reference && (
                        <div className="text-xs text-gray-400">{sale.watch_reference}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">
                        {sale.client_name || 'Walk-in'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-bold text-gray-900">
                        ${formatCurrency(sale.sale_price)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`font-bold ${
                        parseFloat(sale.profit) > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        ${formatCurrency(sale.profit)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${
                        parseFloat(sale.profit_margin) > 20
                          ? 'bg-green-100 text-green-700'
                          : parseFloat(sale.profit_margin) > 10
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {(Number(sale.profit_margin) || 0).toFixed(1)}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(sale.sale_date || sale.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/sales/${sale.id}`);
                        }}
                        className="text-slate-900 hover:text-slate-900 font-medium text-sm flex items-center gap-1"
                      >
                        <FileText className="w-4 h-4" />
                        View Invoice
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesPage;
